import { Button, CustomTooltip, TPlacement, UIText2 } from '@cian/ui-kit';
import { useClickController } from '@cian/ui-kit/tooltip/internal';
import { plural } from '@cian/utils';
import { createPortal } from 'react-dom';

import * as styles from './SearchResultsTooltip.css';
import { useSelector } from 'react-redux';
import { selectSearchMeta, selectQuerystring } from 'shared/selectors/searchMeta';
import { cloneElement, ReactElement, useCallback, useMemo } from 'react';
import { selectIsHotelSearch } from 'shared/selectors/jsonQuery';
import { useAppDispatch } from 'shared/hooks';
import { applyFilters } from 'shared/thunks/filters';
import { trackTooltipSearchClick } from './tracking';
import { updateTags } from 'shared/thunks/tags';

interface ISearchResultsTooltipProps {
  open?: boolean;
  placement: TPlacement;
  hostElement?: HTMLElement | null;
  children: ReactElement;
  onApplyClick?: VoidFunction;
}

export const SearchResultsTooltip: React.FC<ISearchResultsTooltipProps> = ({
  open = false,
  placement,
  hostElement,
  children,
  onApplyClick,
}) => {
  const dispatch = useAppDispatch();

  const { data, isLoading } = useSelector(selectSearchMeta);
  const isHotelSearch = useSelector(selectIsHotelSearch);
  const queryString = useSelector(selectQuerystring);

  const { childrenProps, childrenRef, handleClose } = useClickController({ children });

  const handleApplyButtonClick = useCallback(() => {
    dispatch(updateTags());
    dispatch(applyFilters());
    onApplyClick?.();
    trackTooltipSearchClick(queryString);
  }, [dispatch, onApplyClick, queryString]);

  const tooltipText = useMemo(() => {
    const offersCount = data?.count || 0;

    return `Найдено ${offersCount} ${plural(offersCount, ['объявление', 'объявления', 'объявлений'])}`;
  }, [data]);

  const tooltipElement = (
    <CustomTooltip
      open={open}
      anchorRef={childrenRef}
      arrow
      flip
      preventOverflow
      minHeight={56}
      maxWidth={320}
      theme="black"
      placement={placement}
      portal={false}
      content={
        <div>
          {!isHotelSearch && (
            <div className={styles['text']}>
              <UIText2 color="white_100">{tooltipText}</UIText2>
            </div>
          )}

          <Button size="XS" theme="fill_white_secondary" loading={isLoading} onClick={handleApplyButtonClick}>
            Применить
          </Button>
        </div>
      }
      onClose={handleClose}
    />
  );

  return (
    <>
      {cloneElement(children, childrenProps)}
      {hostElement ? createPortal(tooltipElement, hostElement) : tooltipElement}
    </>
  );
};
